import { forwardRef, useId, useState, type InputHTMLAttributes, type KeyboardEvent } from 'react'
import { Eye, EyeOff, ArrowBigUp, Check, X } from 'lucide-react'
import { cn } from '@/lib/cn'

/**
 * The ONE password field (E-UX). Every password input in the app renders through this:
 *  - a show/hide eye toggle (a real button: keyboard operable, `aria-pressed`, labelled);
 *  - a Caps Lock warning while the key is on (announced politely, never blocks typing);
 *  - forwards its ref, so react-hook-form's `register()` works unchanged.
 * Hidden again automatically on submit (the form re-renders the value as dots) is NOT done on
 * purpose: the user chose to see it; the field is cleared by the page after a successful submit.
 */
export const PasswordInput = forwardRef<HTMLInputElement, Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>>(
  function PasswordInput({ className, onKeyDown, onKeyUp, onBlur, ...props }, ref) {
    const [shown, setShown] = useState(false)
    const [caps, setCaps] = useState(false)
    const capsId = useId()
    const readCaps = (e: KeyboardEvent<HTMLInputElement>) => {
      if (typeof e.getModifierState === 'function') setCaps(e.getModifierState('CapsLock'))
    }
    const describedBy = [props['aria-describedby'], caps ? capsId : null].filter(Boolean).join(' ') || undefined
    return (
      <div>
        {/* The box (border, focus halo, invalid edge) is drawn by this wrapper; the input and the eye
            button sit side by side inside it, so the button never covers the text field. */}
        <div className={cn(
          'flex h-9 w-full items-center rounded-md border border-input bg-card shadow-control',
          'transition-[border-color,box-shadow] duration-fast ease-standard hover:border-foreground/40',
          'focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/20',
          'has-[input[aria-invalid=true]]:border-destructive has-[input:disabled]:bg-surface-disabled',
          className,
        )}>
          <input
            {...props}
            ref={ref}
            type={shown ? 'text' : 'password'}
            // Browsers must not try to spell-check or autocapitalise a visible password.
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            aria-describedby={describedBy}
            className="h-full min-w-0 flex-1 rounded-l-md border-0 bg-transparent px-3 text-sm text-foreground outline-none placeholder:text-content-placeholder disabled:cursor-not-allowed disabled:text-content-disabled"
            onKeyDown={(e) => { readCaps(e); onKeyDown?.(e) }}
            onKeyUp={(e) => { readCaps(e); onKeyUp?.(e) }}
            onBlur={(e) => { setCaps(false); onBlur?.(e) }}
          />
          <button
            type="button"
            onClick={() => setShown((s) => !s)}
            aria-label={shown ? 'Hide password' : 'Show password'}
            aria-pressed={shown}
            title={shown ? 'Hide password' : 'Show password'}
            disabled={props.disabled}
            className="mr-0.5 grid size-8 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors duration-fast hover:bg-surface-hover hover:text-foreground disabled:opacity-40"
          >
            {shown ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
          </button>
        </div>
        {caps && (
          <span id={capsId} role="status" className="mt-1 flex items-center gap-1 text-xs font-medium text-warning-content">
            <ArrowBigUp className="size-3.5" /> Caps Lock is on
          </span>
        )}
      </div>
    )
  },
)

;(PasswordInput as unknown as { formControl: boolean }).formControl = true

// ───────────────────────── strength + rules ─────────────────────────

/** Server policy (ChangePasswordRequest / ResetPasswordRequest / invite accept): 8–255 characters. */
export const PASSWORD_MIN = 8

export interface PasswordRule { id: string; label: string; ok: boolean; required: boolean }

export function passwordRules(pw: string, opts: { email?: string | null; current?: string } = {}): PasswordRule[] {
  const local = (opts.email ?? '').split('@')[0]?.toLowerCase() ?? ''
  const rules: PasswordRule[] = [
    { id: 'len', label: `At least ${PASSWORD_MIN} characters`, ok: pw.length >= PASSWORD_MIN, required: true },
    { id: 'case', label: 'Upper- and lower-case letters', ok: /[a-z]/.test(pw) && /[A-Z]/.test(pw), required: false },
    { id: 'digit', label: 'A number', ok: /\d/.test(pw), required: false },
    { id: 'symbol', label: 'A symbol such as ! @ # -', ok: /[^A-Za-z0-9]/.test(pw), required: false },
    { id: 'email', label: 'Does not contain your e-mail name', ok: pw.length > 0 && (!local || local.length < 3 || !pw.toLowerCase().includes(local)), required: false },
  ]
  if (opts.current !== undefined) {
    rules.push({ id: 'diff', label: 'Different from your current password', ok: pw.length === 0 || pw !== opts.current, required: true })
  }
  return rules
}

/** 0 (empty) … 4 (strong). Length counts most; a repeated or tiny alphabet is penalised. */
export function passwordScore(pw: string): 0 | 1 | 2 | 3 | 4 {
  if (!pw) return 0
  let s = 0
  if (pw.length >= PASSWORD_MIN) s++
  if (pw.length >= 12) s++
  const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((r) => r.test(pw)).length
  if (classes >= 3) s++
  if (classes === 4 && pw.length >= 10) s++
  if (/^(.)\1+$/.test(pw) || /^(password|12345678|qwerty|letmein|welcome)/i.test(pw)) s = 1
  if (pw.length < PASSWORD_MIN) s = Math.min(s, 1)
  return Math.max(1, Math.min(4, s)) as 1 | 2 | 3 | 4
}

const SCORE_LABEL = ['', 'Weak', 'Fair', 'Good', 'Strong'] as const
const SCORE_BAR = ['', 'bg-danger', 'bg-warning', 'bg-info', 'bg-success'] as const
const SCORE_TEXT = ['', 'text-danger-content', 'text-warning-content', 'text-info-content', 'text-success-content'] as const

/** Live strength meter + rule checklist, shown under a NEW-password field. */
export function PasswordStrength({ password, email, current, className }: { password: string; email?: string | null; current?: string; className?: string }) {
  const score = passwordScore(password)
  const rules = passwordRules(password, { email, current })
  return (
    <div className={cn('space-y-1.5', className)} data-testid="password-strength">
      <div className="flex items-center gap-2">
        <div className="grid flex-1 grid-cols-4 gap-1" aria-hidden>
          {[1, 2, 3, 4].map((i) => (
            <span key={i} className={cn('h-1.5 rounded-full', score >= i ? SCORE_BAR[score] : 'bg-muted')} />
          ))}
        </div>
        <span className={cn('w-14 text-right text-xs font-medium', score ? SCORE_TEXT[score] : 'text-muted-foreground')} aria-live="polite">
          {score ? SCORE_LABEL[score] : ''}
        </span>
      </div>
      <ul className="grid grid-cols-1 gap-x-4 gap-y-0.5 sm:grid-cols-2" aria-label="Password rules">
        {rules.map((r) => (
          <li key={r.id} className={cn('flex items-center gap-1.5 text-xs', r.ok ? 'text-success-content' : 'text-muted-foreground')}>
            {r.ok ? <Check className="size-3.5" aria-hidden /> : <X className="size-3.5" aria-hidden />}
            <span>{r.label}{r.required ? '' : ' (recommended)'}</span>
            <span className="sr-only">{r.ok ? 'met' : 'not met'}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** "Passwords match" / "do not match" under a confirm field (only once something is typed). */
export function PasswordMatch({ password, confirm }: { password: string; confirm: string }) {
  if (!confirm) return null
  const ok = password === confirm
  return (
    <span role="status" className={cn('flex items-center gap-1 text-xs font-medium', ok ? 'text-success-content' : 'text-danger-content')}>
      {ok ? <Check className="size-3.5" aria-hidden /> : <X className="size-3.5" aria-hidden />}
      {ok ? 'Passwords match' : 'Passwords do not match yet'}
    </span>
  )
}

/** True when the password satisfies every REQUIRED rule (the same ones the server enforces). */
export function passwordAcceptable(pw: string, opts: { current?: string } = {}): boolean {
  return passwordRules(pw, opts).every((r) => !r.required || r.ok)
}
